import React, { useState, useRef } from 'react';
import { SponsorMetadata, SponsorMessageTemplate } from '../../types/sponsor';
import { Image, Link, Clock, MessageSquare, Upload, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface SponsorFormProps {
  initialSponsor?: {
    metadata: SponsorMetadata;
    start_time: string;
    end_time: string;
    message_template_id?: string;
    active: boolean;
  };
  messageTemplates: SponsorMessageTemplate[];
  onSubmit: (data: {
    metadata: SponsorMetadata;
    start_time: string;
    end_time: string;
    message_template_id?: string;
    active: boolean;
  }) => void;
}

export function SponsorForm({ initialSponsor, messageTemplates, onSubmit }: SponsorFormProps) {
  const [name, setName] = useState(initialSponsor?.metadata.name || '');
  const [logoUrl, setLogoUrl] = useState(initialSponsor?.metadata.logo_url || '');
  const [website, setWebsite] = useState(initialSponsor?.metadata.website || '');
  const [description, setDescription] = useState(initialSponsor?.metadata.description || '');
  const [startTime, setStartTime] = useState(initialSponsor?.start_time || '');
  const [endTime, setEndTime] = useState(initialSponsor?.end_time || '');
  const [messageTemplateId, setMessageTemplateId] = useState(initialSponsor?.message_template_id || '');
  const [active, setActive] = useState(initialSponsor?.active ?? true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('File size must be less than 2MB');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      if (!supabase) {
        throw new Error('Storage not available');
      }

      // Upload file to Supabase storage
      const fileName = `${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from('sponsor-logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('sponsor-logos')
        .getPublicUrl(data.path);

      // Update form state
      setLogoUrl(publicUrl);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to upload file');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!logoUrl) {
      setUploadError('Logo URL is required');
      return;
    }
    onSubmit({
      metadata: {
        name,
        logo_url: logoUrl,
        website,
        description
      },
      start_time: startTime,
      end_time: endTime,
      message_template_id: messageTemplateId || undefined,
      active
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Preview current sponsor if exists */}
      {initialSponsor && (
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Current Sponsor</h4>
          <div className="flex items-center gap-4">
            <img
              src={initialSponsor.metadata.logo_url}
              alt={`${initialSponsor.metadata.name} logo`}
              className="h-8 w-auto object-contain"
            />
            <div>
              <div className="font-medium">{initialSponsor.metadata.name}</div>
              {initialSponsor.metadata.website && (
                <a
                  href={initialSponsor.metadata.website}
                  target="_blank"
                  rel="noopener noreferrer" 
                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <Link className="w-3 h-3" />
                  Visit Website
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Sponsor Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          <div className="flex items-center gap-2">
            <Image className="w-4 h-4" />
            Logo URL
          </div>
        </label>
        <div className="mt-1 space-y-2">
          {logoUrl && (
            <div className="flex items-center gap-4 p-2 bg-gray-50 rounded-lg">
              <img
                src={logoUrl}
                alt="Sponsor logo preview"
                className="h-8 w-auto object-contain"
              />
              <button
                type="button"
                onClick={() => setLogoUrl('')}
                className="text-gray-400 hover:text-red-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <input
                type="url"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="Enter logo URL or upload a file"
                required
              />
            </div>
            <div className="relative">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={isUploading}
              />
              <button
                type="button"
                className={`px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  isUploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={isUploading}
              >
                <Upload className="w-4 h-4" />
              </button>
            </div>
          </div>
          {uploadError && (
            <p className="text-sm text-red-600">{uploadError}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          <div className="flex items-center gap-2">
            <Link className="w-4 h-4" />
            Website
          </div>
        </label>
        <input
          type="url"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Start Time
            </div>
          </label>
          <input
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              End Time
            </div>
          </label>
          <input
            type="datetime-local"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Message Template
          </div>
        </label>
        <select
          value={messageTemplateId}
          onChange={(e) => setMessageTemplateId(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
        >
          <option value="">No automated messages</option>
          {messageTemplates.map(template => (
            <option key={template.id} value={template.id}>
              {template.message.substring(0, 50)}...
              {template.active ? '' : ' (Inactive)'}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <label className="ml-2 block text-sm text-gray-900">
          Active
        </label>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          Save Sponsor
        </button>
      </div>
    </form>
  );
}